import { css } from "styled-system/css";

// Editorial page styles: rules over cards, serif headlines and prose, sans
// labels, mono numbers. Black and white; color only arrives through the
// figures' emphasis marks and the caution tint on a real alert.
//
// One module for every editorial page. Where two pages settled on different
// sizes for the same piece, both are kept under distinct names (for
// example `headline` and `headlineCompact`) so each page renders as it did.
//
//   ed     page frame, masthead, headings, prose, notes, layout grids
//   btn    actions
//   field  search, inputs, selects and the segmented filter
//   tbl    tables and the small labels that sit in table cells

export const ed = {
  page: css({
    bg: "ed.paper",
    color: "ed.ink",
    fontFamily: "sans",
    // Bleed past the layout's padding (16px on phones, 24px up) so the
    // paper fills the pane without a sideways scroll
    m: { base: "-16px", md: "-24px" },
    px: { base: "16px", md: "32px" },
    pb: "64px",
    // Fill the pane below the 65px app header
    minHeight: "calc(100vh - 65px)",
    "& a": { color: "inherit" },
  }),
  inner: css({ maxWidth: "1180px", mx: "auto" }),

  /* ---- Masthead ---- */

  /** One rule across the top: section nav left, period right. */
  masthead: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "4px 16px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.ink",
    py: "10px",
    fontFamily: "sans",
    fontSize: "11px",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "ed.ink2",
  }),
  mastTitle: css({ fontWeight: 700, color: "ed.ink" }),
  mastNav: css({
    display: "flex",
    gap: "14px",
    "& a": {
      textDecoration: "none",
      color: "ed.ink3",
      pb: "2px",
      borderBottom: "2px solid transparent",
      _hover: { color: "ed.ink" },
    },
    "& a[aria-current=page]": { color: "ed.ink", borderColor: "ed.ink" },
  }),
  mastMeta: css({
    fontFamily: "mono",
    letterSpacing: "0.02em",
    textTransform: "none",
    fontVariantNumeric: "tabular-nums",
  }),
  /** Masthead whose nav reads as tabs sitting on the rule (the /code hub). */
  tabMasthead: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "4px 16px",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.ink",
    pt: "10px",
    fontFamily: "sans",
    fontSize: "11px",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: "ed.ink2",
  }),
  tabNav: css({
    display: "flex",
    flexWrap: "wrap",
    gap: "4px 18px",
    "& a": {
      textDecoration: "none",
      color: "ed.ink3",
      pb: "8px",
      borderBottom: "2px solid transparent",
      mb: "-1px",
    },
    "& a:hover": { color: "ed.ink" },
    "& a[aria-current=page]": {
      color: "ed.ink",
      borderColor: "ed.ink",
      fontWeight: 700,
    },
  }),
  tabMeta: css({
    fontFamily: "mono",
    letterSpacing: "0.02em",
    textTransform: "none",
    fontVariantNumeric: "tabular-nums",
    pb: "8px",
  }),
  /** "← All reporters" link above a detail page's kicker. */
  back: css({
    display: "inline-block",
    mt: "12px",
    fontFamily: "sans",
    fontSize: "12px",
    color: "ed.ink2",
    textDecoration: "none",
    _hover: { textDecoration: "underline" },
  }),

  /* ---- Headings ---- */

  kicker: css({
    fontFamily: "sans",
    fontWeight: 700,
    fontSize: "12px",
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "ed.ink2",
    mt: "28px",
    mb: "8px",
  }),
  headline: css({
    fontFamily: "serif",
    fontWeight: 700,
    fontSize: { base: "30px", md: "44px" },
    lineHeight: 1.08,
    letterSpacing: "-0.015em",
    m: 0,
    mb: "12px",
    maxWidth: "900px",
    textWrap: "balance",
  }),
  /** A size down from `headline`, for hub tabs under a tab masthead. */
  headlineCompact: css({
    fontFamily: "serif",
    fontWeight: 700,
    fontSize: { base: "30px", md: "40px" },
    lineHeight: 1.1,
    letterSpacing: "-0.015em",
    m: 0,
    mb: "12px",
    maxWidth: "900px",
    textWrap: "balance",
  }),
  dek: css({
    fontFamily: "serif",
    fontSize: { base: "17px", md: "19px" },
    lineHeight: 1.45,
    color: "ed.ink2",
    m: 0,
    mb: "14px",
    maxWidth: "760px",
  }),
  byline: css({
    fontFamily: "sans",
    fontSize: "12px",
    color: "ed.ink2",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderColor: "ed.rule",
    pt: "8px",
    mb: "20px",
    "& b": { color: "ed.ink", fontWeight: 600 },
    "& .num": { fontFamily: "mono", fontVariantNumeric: "tabular-nums" },
    "& a": { textDecoration: "underline", textUnderlineOffset: "2px" },
  }),
  /** Byline with a second item pushed right, e.g. a control or a count. */
  bylineSplit: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "8px 16px",
    fontFamily: "sans",
    fontSize: "12px",
    color: "ed.ink2",
    borderTopWidth: "1px",
    borderTopStyle: "solid",
    borderColor: "ed.rule",
    pt: "8px",
    mb: "20px",
    "& b": { color: "ed.ink", fontWeight: 600 },
    "& .num": { fontFamily: "mono", fontVariantNumeric: "tabular-nums" },
  }),
  /** Section label over a heavy ink rule. */
  sect: css({
    fontFamily: "sans",
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    color: "ed.ink3",
    borderTopWidth: "3px",
    borderTopStyle: "solid",
    borderColor: "ed.ink",
    pt: "8px",
    mt: "44px",
  }),
  /** Section label with something on the right (a link or a count). */
  sectRow: css({
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: "12px",
  }),
  h2: css({
    fontFamily: "sans",
    fontWeight: 800,
    fontSize: "22px",
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
    m: 0,
    mt: "6px",
    mb: "8px",
  }),
  h3: css({
    fontFamily: "sans",
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: 1.3,
    color: "ed.ink",
    m: 0,
    mb: "6px",
  }),
  /** Smaller subhead between blocks of one section. */
  h3Sm: css({
    fontFamily: "sans",
    fontWeight: 700,
    fontSize: "14px",
    m: 0,
    mt: "20px",
    mb: "6px",
  }),

  /* ---- Text ---- */

  prose: css({
    fontFamily: "serif",
    fontSize: "16.5px",
    lineHeight: 1.55,
    color: "ed.ink",
    maxWidth: "700px",
    m: 0,
    mb: "16px",
    "& .num": { fontFamily: "mono", fontSize: "0.92em", fontVariantNumeric: "tabular-nums" },
    "& p": { m: 0, mb: "10px" },
    "& p:last-child": { mb: 0 },
  }),
  /** `prose` with its links underlined, for text whose links are the point. */
  proseLinked: css({
    fontFamily: "serif",
    fontSize: "16.5px",
    lineHeight: 1.55,
    color: "ed.ink",
    maxWidth: "700px",
    m: 0,
    mb: "16px",
    "& .num": { fontFamily: "mono", fontSize: "0.92em", fontVariantNumeric: "tabular-nums" },
    "& a": { textDecoration: "underline", textUnderlineOffset: "2px" },
  }),
  /** Sans text a step below prose, for explanations next to a figure. */
  small: css({
    fontFamily: "sans",
    fontSize: "12.5px",
    lineHeight: 1.5,
    color: "ed.ink2",
    maxWidth: "760px",
    m: 0,
    mb: "10px",
    "& .num": { fontFamily: "mono", fontVariantNumeric: "tabular-nums" },
  }),
  /** One-line note under a table or figure: what was counted and how. */
  method: css({
    mt: "8px",
    fontFamily: "sans",
    fontSize: "11.5px",
    lineHeight: 1.5,
    color: "ed.ink3",
    "& .num": { fontFamily: "mono", fontVariantNumeric: "tabular-nums" },
    "& a": {
      color: "ed.ink2",
      textDecoration: "underline",
      textUnderlineOffset: "2px",
    },
  }),
  /** `method` held to the dek's measure, for notes under full-width figures. */
  methodNarrow: css({
    mt: "8px",
    fontFamily: "sans",
    fontSize: "11.5px",
    lineHeight: 1.5,
    color: "ed.ink3",
    maxWidth: "760px",
    "& .num": { fontFamily: "mono", fontVariantNumeric: "tabular-nums" },
    "& a": {
      color: "ed.ink2",
      textDecoration: "underline",
      textUnderlineOffset: "2px",
    },
  }),
  /** The method note set in mono, for pages whose notes are mostly figures. */
  methodMono: css({
    mt: "8px",
    fontFamily: "mono",
    fontSize: "10.5px",
    lineHeight: 1.5,
    color: "ed.ink3",
    fontVariantNumeric: "tabular-nums",
    maxWidth: "900px",
  }),
  bullets: css({
    fontFamily: "sans",
    fontSize: "12.5px",
    lineHeight: 1.5,
    color: "ed.ink2",
    maxWidth: "820px",
    m: 0,
    mb: "12px",
    pl: "18px",
    listStyleType: "square",
    "& li": { mb: "4px" },
    "& .num": { fontFamily: "mono", fontVariantNumeric: "tabular-nums" },
  }),
  defs: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", md: "1fr 1fr" },
    gap: "4px 32px",
    fontFamily: "sans",
    fontSize: "12.5px",
    lineHeight: 1.5,
    color: "ed.ink2",
    m: 0,
    "& dt": { fontWeight: 700, color: "ed.ink", mt: "8px" },
    "& dd": { m: 0 },
  }),
  muted: css({ color: "ed.ink3" }),
  num: css({ fontFamily: "mono", fontVariantNumeric: "tabular-nums" }),

  /* ---- States ---- */

  /** Whole-page loading line. */
  loading: css({
    fontFamily: "mono",
    fontSize: "12px",
    color: "ed.ink3",
    py: "48px",
  }),
  /** Loading line inside one section. */
  loadingSm: css({
    fontFamily: "mono",
    fontSize: "12px",
    color: "ed.ink3",
    py: "24px",
  }),
  empty: css({
    fontFamily: "sans",
    fontSize: "13px",
    color: "ed.ink3",
    py: "10px",
    m: 0,
  }),
  /** A real alert: faint caution tint, ink text, no icon color. */
  notice: css({
    bg: "ed.cautionTint",
    color: "ed.ink",
    fontFamily: "sans",
    fontSize: "13px",
    lineHeight: 1.45,
    px: "12px",
    py: "8px",
    mt: "16px",
    "& b": { fontWeight: 700 },
    "& .num": { fontFamily: "mono", fontVariantNumeric: "tabular-nums" },
  }),
  /** The one-line form of `notice`, under a headline. */
  alert: css({
    fontFamily: "sans",
    fontSize: "13px",
    color: "ed.ink",
    bg: "ed.cautionTint",
    px: "10px",
    py: "6px",
    mt: "12px",
    maxWidth: "760px",
    "& .num": { fontFamily: "mono", fontVariantNumeric: "tabular-nums" },
  }),

  /* ---- Layout ---- */

  twoUp: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", lg: "minmax(0, 3fr) minmax(0, 2fr)" },
    gap: "28px",
    alignItems: "start",
  }),
  halves: css({
    display: "grid",
    gridTemplateColumns: { base: "1fr", lg: "1fr 1fr" },
    gap: "28px",
    alignItems: "start",
    "& > *": { minWidth: 0 },
  }),
};

/** Ink actions with square corners. */
export const btn = {
  /** Primary: ink fill. */
  primary: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    px: "12px",
    py: "6px",
    fontFamily: "sans",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    // `!` so the page's `& a { color: inherit }` doesn't win on a link button
    color: "ed.paper!",
    bg: "ed.ink",
    border: "1px solid",
    borderColor: "ed.ink",
    borderRadius: 0,
    cursor: "pointer",
    whiteSpace: "nowrap",
    textDecoration: "none",
    transition: "opacity 300ms cubic-bezier(0.2, 0, 0, 1)",
    _hover: { opacity: 0.85 },
    _disabled: { opacity: 0.4, cursor: "not-allowed" },
    _focusVisible: { outline: "2px solid var(--colors-ed-ink)", outlineOffset: "2px" },
  }),
  /** Secondary: outlined ink, same size as primary. */
  secondary: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    px: "12px",
    py: "6px",
    fontFamily: "sans",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "ed.ink",
    bg: "transparent",
    border: "1px solid",
    borderColor: "ed.ink",
    borderRadius: 0,
    cursor: "pointer",
    whiteSpace: "nowrap",
    textDecoration: "none",
    transition: "background 300ms cubic-bezier(0.2, 0, 0, 1)",
    _hover: { bg: "ed.panel" },
    _disabled: { opacity: 0.4, cursor: "not-allowed" },
    _focusVisible: { outline: "2px solid var(--colors-ed-ink)", outlineOffset: "2px" },
  }),
  /** Small outlined action or toggle; `aria-pressed=true` fills it. */
  outline: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: "sans",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "ed.ink",
    bg: "transparent",
    border: "1px solid",
    borderColor: "ed.ink",
    borderRadius: 0,
    px: "10px",
    py: "4px",
    cursor: "pointer",
    whiteSpace: "nowrap",
    _hover: { bg: "ed.panel" },
    _disabled: {
      opacity: 0.4,
      cursor: "not-allowed",
      _hover: { bg: "transparent" },
    },
    "&[aria-pressed=true]": { bg: "ed.ink", color: "ed.paper" },
  }),
  /** Primary with a sentence-case label, for actions inside running text. */
  sentence: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: "sans",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.04em",
    px: "12px",
    py: "6px",
    bg: "ed.ink",
    color: "ed.paper",
    border: "1px solid",
    borderColor: "ed.ink",
    borderRadius: "0",
    cursor: "pointer",
    transition: "opacity 300ms cubic-bezier(0.2, 0, 0, 1)",
    _hover: { opacity: 0.85 },
    _disabled: { opacity: 0.5, cursor: "not-allowed" },
  }),
  /** Small outlined action with a sentence-case label. */
  sentenceOutline: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    fontFamily: "sans",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.04em",
    px: "10px",
    py: "4px",
    bg: "transparent",
    color: "ed.ink",
    border: "1px solid",
    borderColor: "ed.ink",
    borderRadius: "0",
    cursor: "pointer",
    transition: "background 300ms cubic-bezier(0.2, 0, 0, 1)",
    _hover: { bg: "ed.panel" },
    _disabled: { opacity: 0.5, cursor: "not-allowed" },
  }),
  /** Small square icon button, outlined. */
  icon: css({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "26px",
    height: "26px",
    color: "ed.ink2",
    bg: "transparent",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: 0,
    cursor: "pointer",
    _hover: { color: "ed.ink", borderColor: "ed.ink" },
    _disabled: { opacity: 0.4, cursor: "not-allowed" },
  }),
};

/** Inputs: square corners, hairline border. */
export const field = {
  /** Search as an underlined line, no box. */
  search: css({
    fontFamily: "sans",
    fontSize: "13px",
    color: "ed.ink",
    bg: "transparent",
    border: "none",
    borderBottomWidth: "1px",
    borderBottomStyle: "solid",
    borderColor: "ed.ink",
    borderRadius: 0,
    px: 0,
    py: "4px",
    width: "220px",
    outline: "none",
    _placeholder: { color: "ed.ink3" },
    _focusVisible: { borderBottomWidth: "2px" },
  }),
  input: css({
    fontFamily: "sans",
    fontSize: "13px",
    color: "ed.ink",
    bg: "ed.paper",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: 0,
    px: "8px",
    py: "6px",
    width: "100%",
    outline: "none",
    _focus: { borderColor: "ed.ink" },
    _placeholder: { color: "ed.ink3" },
  }),
  select: css({
    fontFamily: "sans",
    fontSize: "12px",
    color: "ed.ink",
    bg: "ed.paper",
    border: "1px solid",
    borderColor: "ed.rule",
    borderRadius: 0,
    px: "8px",
    py: "4px",
    cursor: "pointer",
    outline: "none",
    _focusVisible: { borderColor: "ed.ink" },
  }),
  /** Segmented filter rendered as uppercase tabs with an ink underline. */
  segs: css({
    display: "flex",
    gap: "14px",
    "& button": {
      all: "unset",
      cursor: "pointer",
      fontFamily: "sans",
      fontSize: "11px",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "ed.ink3",
      pb: "3px",
      borderBottom: "2px solid transparent",
    },
    "& button:hover": { color: "ed.ink" },
    "& button:focus-visible": { outline: "1px solid var(--colors-ed-ink)" },
    "& button[aria-pressed=true]": { color: "ed.ink", borderColor: "ed.ink" },
  }),
};

/**
 * Tables. `table` is the dense default: uppercase letter-spaced sans header
 * over an ink rule, sans cells, mono numbers right-aligned via `data-num`.
 * `numeric` sets every cell in mono for tables that are mostly figures;
 * `simple` is a looser 13px list table.
 */
export const tbl = {
  /** Row of filters and counts above a table. */
  controls: css({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px 16px",
    py: "8px",
    fontFamily: "sans",
    fontSize: "12px",
    color: "ed.ink2",
  }),
  count: css({ fontFamily: "mono", fontVariantNumeric: "tabular-nums", color: "ed.ink3" }),
  /** Horizontal scroll with the table's heavy top rule. */
  scroll: css({ overflowX: "auto", borderTopWidth: "2px", borderTopStyle: "solid", borderColor: "ed.ink" }),
  /** Lets a wide table scroll inside its column on a phone, no rule. */
  scrollPlain: css({ overflowX: "auto", maxWidth: "100%" }),
  table: css({
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "12px",
    "& th": {
      fontFamily: "sans",
      fontSize: "10px",
      fontWeight: 700,
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      color: "ed.ink3",
      textAlign: "left",
      whiteSpace: "nowrap",
      py: "6px",
      px: "6px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.ink",
      verticalAlign: "bottom",
    },
    "& th button": {
      all: "unset",
      cursor: "pointer",
      _hover: { color: "ed.ink" },
      _focusVisible: { outline: "1px solid var(--colors-ed-ink)" },
    },
    "& th[data-active]": { color: "ed.ink" },
    "& td": {
      fontFamily: "sans",
      textAlign: "left",
      py: "5px",
      px: "6px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.grid",
      color: "ed.ink",
      verticalAlign: "top",
    },
    "& [data-num]": {
      textAlign: "right",
      fontFamily: "mono",
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    },
    "& [data-mono]": { fontFamily: "mono", fontVariantNumeric: "tabular-nums" },
    "& td[data-flag]": { bg: "ed.cautionTint" },
    "& tbody tr:hover td": { bg: "ed.panel" },
    "& td a": {
      fontWeight: 600,
      textDecoration: "none",
      _hover: { textDecoration: "underline" },
    },
  }),
  /** Every cell in mono; `data-text` switches a cell back to sans. */
  numeric: css({
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "12px",
    "& th": {
      fontFamily: "sans",
      fontSize: "10px",
      fontWeight: 700,
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      color: "ed.ink3",
      textAlign: "left",
      whiteSpace: "nowrap",
      py: "6px",
      px: "6px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.ink",
      verticalAlign: "bottom",
    },
    "& th button": { all: "unset", cursor: "pointer" },
    "& th button:hover": { color: "ed.ink" },
    "& th button:focus-visible": { outline: "1px solid var(--colors-ed-ink)" },
    "& th[aria-sort=ascending], & th[aria-sort=descending]": {
      color: "ed.ink",
    },
    "& td": {
      fontFamily: "mono",
      fontVariantNumeric: "tabular-nums",
      textAlign: "left",
      whiteSpace: "nowrap",
      py: "4px",
      px: "6px",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.grid",
      color: "ed.ink",
      verticalAlign: "baseline",
    },
    "& [data-num]": { textAlign: "right" },
    "& td[data-text]": { fontFamily: "sans", whiteSpace: "normal" },
    "& td[data-flag]": { bg: "ed.cautionTint" },
    "& td[data-dim], & span[data-dim]": { color: "ed.ink3" },
    "& tbody tr:hover td": { bg: "ed.panel" },
    "& td a": { textDecoration: "none" },
    // Spelled out: a nested _hover here underlined every link when the table was hovered
    "& td a:hover": { textDecoration: "underline" },
  }),
  /** Looser list table: 13px rows, sans header, `data-id` for keys. */
  simple: css({
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "13px",
    "& th": {
      textAlign: "left",
      fontFamily: "sans",
      fontWeight: 600,
      fontSize: "10.5px",
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "ed.ink2",
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.ink",
      py: "5px",
      pr: "12px",
      whiteSpace: "nowrap",
    },
    "& td": {
      borderBottomWidth: "1px",
      borderBottomStyle: "solid",
      borderColor: "ed.rule",
      py: "6px",
      pr: "12px",
      color: "ed.ink",
      verticalAlign: "baseline",
    },
    "& th:last-child, & td:last-child": { pr: 0 },
    "& [data-num]": {
      textAlign: "right",
      fontFamily: "mono",
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
    },
    "& th[data-num]": { fontFamily: "sans" },
    "& [data-id]": {
      fontFamily: "mono",
      fontSize: "12px",
      color: "ed.ink2",
      whiteSpace: "nowrap",
    },
    "& a": {
      textDecoration: "none",
      _hover: { textDecoration: "underline", textUnderlineOffset: "2px" },
    },
  }),
  /** Clip a long title cell to one line. */
  clipCell: css({
    maxWidth: "0",
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  rank: css({ color: "ed.ink3", width: "28px" }),
  sub: css({ ml: "6px", fontSize: "10.5px", color: "ed.ink3", fontWeight: 400 }),
  subBlock: css({ display: "block", fontSize: "10.5px", color: "ed.ink3", fontWeight: 400 }),
  /** Small trailing figure after a number, like the roster's deltas. */
  aside: css({
    display: "inline-block",
    ml: "4px",
    fontSize: "10.5px",
    color: "ed.ink3",
    fontFamily: "mono",
  }),
  /** An outlined ink label: the editorial replacement for a status pill. */
  label: css({
    display: "inline-block",
    px: "5px",
    py: "0",
    fontFamily: "sans",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "ed.ink",
    border: "1px solid",
    borderColor: "ed.ink",
    lineHeight: "16px",
    whiteSpace: "nowrap",
  }),
  /** A plain uppercase tag with no box, for the quieter state. */
  tag: css({
    display: "inline-block",
    fontFamily: "sans",
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "ed.ink3",
    lineHeight: "16px",
    whiteSpace: "nowrap",
  }),
  /** Gray outlined label, for a type that used to be a pill. */
  tagOutline: css({
    display: "inline-block",
    fontFamily: "sans",
    fontSize: "10px",
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink2",
    border: "1px solid",
    borderColor: "ed.ink3",
    px: "4px",
    lineHeight: "16px",
    whiteSpace: "nowrap",
  }),
  /** Health flag after a value; `data-tone` is quiet or solid. */
  flag: css({
    display: "inline-block",
    fontFamily: "sans",
    fontSize: "9.5px",
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    lineHeight: 1,
    color: "ed.ink",
    border: "1px solid",
    borderColor: "ed.ink",
    px: "4px",
    py: "2px",
    ml: "6px",
    whiteSpace: "nowrap",
    verticalAlign: "1px",
    "&[data-tone=quiet]": { color: "ed.ink3", borderColor: "ed.rule" },
    "&[data-tone=solid]": { bg: "ed.ink", color: "ed.paper" },
  }),
  pager: css({
    display: "flex",
    alignItems: "center",
    gap: "12px",
    mt: "12px",
    fontFamily: "mono",
    fontSize: "12px",
    color: "ed.ink2",
  }),
};
